import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Avatar,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { usePublicCompanies } from '../../hooks/usePublicCompanies';
import { PublicCompanyItem } from '../../services/publicCompanyService';

// ─── グラデーションパレット（画像なし企業のプレースホルダー用） ───
const CARD_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

// ─── 公開モード(Pinterest風)用の画像高さバリエーション ─────────
const PUBLIC_IMAGE_HEIGHTS = [150, 180, 210, 240];

const getPublicImageHeight = (companyId: number): number => {
  // companyId をベースに決定論的にパターンを選択（同じ企業は常に同じ高さ）
  return PUBLIC_IMAGE_HEIGHTS[companyId % PUBLIC_IMAGE_HEIGHTS.length];
};

// グリッドで扱う企業項目（管理者プレビューでは isPublished を含む）
export type CompanyGridItem = PublicCompanyItem & { isPublished?: boolean };

// ─── 個別カード ───────────────────────────────────────────────
interface CompanyCardProps {
  company: CompanyGridItem;
  onDetailsClick: (companyId: number) => void;
  gradientIndex: number;
  mode: 'public' | 'authenticated';
}

const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  onDetailsClick,
  gradientIndex,
  mode,
}) => {
  const [imgError, setImgError] = useState(false);
  const hasHeroImage = !!(company.heroImageUrl && !imgError);
  const gradient = CARD_GRADIENTS[gradientIndex % CARD_GRADIENTS.length];
  const locationLabel = [company.prefecture, company.city]
    .filter(Boolean)
    .join('');

  // 画像高さ: public はバリエーション、authenticated は固定
  const imageHeight =
    mode === 'public' ? getPublicImageHeight(company.id) : 180;

  // タグ表示: public は最大3件、authenticated は業種1+職種1+overflow
  const allJobs = company.jobTypes ?? [];
  const tagsForPublic: string[] = [
    ...(company.businessType ? [company.businessType] : []),
    ...allJobs,
  ].slice(0, 3);
  const primaryTag = company.businessType ?? undefined;
  const secondaryTag = allJobs[0];
  const overflowCount = Math.max(0, allJobs.length - 1);

  const handleClick = () => onDetailsClick(company.id);

  const ariaLabel =
    mode === 'public'
      ? `${company.companyName}の詳細を見る（ログインが必要です）`
      : `${company.companyName}の詳細を見る`;

  return (
    <Box
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={ariaLabel}
      sx={{
        // public: masonry の inline-block / authenticated: grid セル内で縦伸び
        display: mode === 'public' ? 'inline-block' : 'flex',
        flexDirection: 'column',
        width: '100%',
        height: mode === 'authenticated' ? '100%' : 'auto',
        breakInside: 'avoid',
        mb: mode === 'public' ? 2 : 0,
        borderRadius: 2,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          transform: 'translateY(-3px)',
          '& .details-link': { opacity: 1 },
        },
        '&:focus-visible': {
          outline: '2px solid #667eea',
          outlineOffset: 2,
        },
      }}
    >
      {/* ─── ヒーロー画像 ─── */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: imageHeight,
          flexShrink: 0,
          background: hasHeroImage ? 'transparent' : gradient,
          overflow: 'hidden',
        }}
      >
        {hasHeroImage && (
          <Box
            component="img"
            src={company.heroImageUrl}
            alt={company.companyName}
            loading="lazy"
            decoding="async"
            onError={() => setImgError(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        )}

        {/* グラデーションオーバーレイ */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: hasHeroImage
              ? 'linear-gradient(to top, rgba(0,0,0,0.62) 0%, rgba(0,0,0,0.08) 55%, transparent 100%)'
              : 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />

        {/* バッジ群（注目 / 非公開） */}
        {(company.isSponsor || company.isPublished === false) && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            {company.isSponsor && (
              <Box
                sx={{
                  bgcolor: 'rgba(255,193,7,0.95)',
                  color: '#5a3e00',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  letterSpacing: '0.04em',
                }}
              >
                注目
              </Box>
            )}
            {company.isPublished === false && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  bgcolor: 'rgba(33,33,33,0.85)',
                  color: '#fff',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  letterSpacing: '0.04em',
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: '0.72rem' }} />
                非公開
              </Box>
            )}
          </Box>
        )}

        {/* ロゴアイコン（右上） */}
        {company.logoUrl && (
          <Avatar
            src={company.logoUrl}
            alt={company.companyName}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 36,
              height: 36,
              border: '2px solid rgba(255,255,255,0.85)',
              bgcolor: 'white',
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
            }}
          />
        )}

        {/* 企業名 + 所在地（画像下部オーバーレイ） */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            px: 1.5,
            pb: 1.25,
          }}
        >
          <Typography
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.95rem',
              lineHeight: 1.3,
              textShadow: '0 1px 4px rgba(0,0,0,0.4)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {company.companyName}
          </Typography>
          {locationLabel && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                mt: 0.25,
              }}
            >
              <LocationOnIcon
                sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)' }}
              />
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                }}
              >
                {locationLabel}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* ─── カード本文 ─── */}
      <Box
        sx={{
          p: 1.5,
          pt: 1.25,
          flex: mode === 'authenticated' ? 1 : 'initial',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 事業内容プレビュー */}
        {company.businessContentPreview && (
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: '#555',
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 1,
              // authenticated: 3行分の高さを固定確保（カード高さ揃え用）
              ...(mode === 'authenticated' && {
                minHeight: 'calc(0.8rem * 1.6 * 3)',
              }),
            }}
          >
            {company.businessContentPreview}
          </Typography>
        )}

        {/* タグ */}
        {mode === 'public' ? (
          // public: 最大3件、複数行可
          tagsForPublic.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {tagsForPublic.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.68rem',
                    bgcolor: 'rgba(102,126,234,0.1)',
                    color: '#5a4a9a',
                    fontWeight: 500,
                    border: '1px solid rgba(102,126,234,0.2)',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              ))}
            </Box>
          )
        ) : (
          // authenticated: 業種1+職種1+overflow件数、必ず1行・高さ固定
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 0.5,
              mb: 1,
              minHeight: 20,
              overflow: 'hidden',
              alignItems: 'center',
            }}
          >
            {primaryTag && (
              <Chip
                label={primaryTag}
                size="small"
                sx={{
                  height: 20,
                  maxWidth: '50%',
                  fontSize: '0.68rem',
                  bgcolor: 'rgba(102,126,234,0.1)',
                  color: '#5a4a9a',
                  fontWeight: 500,
                  border: '1px solid rgba(102,126,234,0.2)',
                  '& .MuiChip-label': {
                    px: 0.75,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  },
                }}
              />
            )}
            {secondaryTag && (
              <Chip
                label={secondaryTag}
                size="small"
                sx={{
                  height: 20,
                  maxWidth: '40%',
                  fontSize: '0.68rem',
                  bgcolor: 'rgba(102,126,234,0.06)',
                  color: '#5a4a9a',
                  fontWeight: 500,
                  border: '1px solid rgba(102,126,234,0.18)',
                  '& .MuiChip-label': {
                    px: 0.75,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  },
                }}
              />
            )}
            {overflowCount > 0 && (
              <Typography
                sx={{
                  fontSize: '0.68rem',
                  color: 'text.secondary',
                  fontWeight: 500,
                  flexShrink: 0,
                }}
              >
                +{overflowCount}
              </Typography>
            )}
          </Box>
        )}

        {/* 従業員数（authenticated 時は常に高さ確保） */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 1,
            minHeight: mode === 'authenticated' ? 18 : 'auto',
          }}
        >
          {company.employeeCountRange && (
            <>
              <PeopleOutlineIcon
                sx={{ fontSize: '0.85rem', color: 'text.secondary' }}
              />
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {company.employeeCountRange}
              </Typography>
            </>
          )}
        </Box>

        {/* 詳細リンク（モードで文言・アイコン切替） */}
        <Box
          className="details-link"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            opacity: 0.75,
            transition: 'opacity 0.2s',
            mt: mode === 'authenticated' ? 'auto' : 0.5,
            pt: mode === 'authenticated' ? 0.5 : 0,
          }}
        >
          {mode === 'public' && (
            <LockOutlinedIcon sx={{ fontSize: '0.78rem', color: '#667eea' }} />
          )}
          <Typography
            sx={{
              fontSize: '0.78rem',
              color: '#667eea',
              fontWeight: 600,
            }}
          >
            {mode === 'public' ? '新規登録して詳細を見る' : '詳細を見る'}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: '0.78rem', color: '#667eea' }} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── スケルトンカード ──────────────────────────────────────────
const CardSkeleton: React.FC<{ mode: 'public' | 'authenticated' }> = ({
  mode,
}) => (
  <Box
    sx={{
      display: mode === 'public' ? 'inline-block' : 'flex',
      flexDirection: 'column',
      width: '100%',
      height: mode === 'authenticated' ? '100%' : 'auto',
      breakInside: 'avoid',
      mb: mode === 'public' ? 2 : 0,
      borderRadius: 2,
      overflow: 'hidden',
      background: '#fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    }}
  >
    <Skeleton variant="rectangular" height={180} />
    <Box sx={{ p: 1.5, flex: mode === 'authenticated' ? 1 : 'initial' }}>
      <Skeleton variant="text" width="70%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="100%" />
      <Skeleton variant="text" width="80%" />
      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
        <Skeleton variant="rounded" width={56} height={20} />
        <Skeleton variant="rounded" width={56} height={20} />
      </Box>
    </Box>
  </Box>
);

// ─── メインコンポーネント ──────────────────────────────────────
interface PublicCompanyGridProps {
  /**
   * カードクリック時の処理。
   * - public モード: companyId は無視され、新規登録画面などへ誘導する想定
   * - authenticated モード: companyId を使って詳細ページへ遷移する想定
   */
  onDetailsClick: (companyId: number) => void;
  /** 表示モード（デフォルト: public） */
  mode?: 'public' | 'authenticated';
  /** セクションタイトル */
  title?: string;
  /** セクションサブタイトル */
  subtitle?: string;
  /** 取得件数の上限（companies が未指定の場合のみ有効） */
  limit?: number;
  /** フッターのサブテキストリンク表示有無（public 時のみ意味あり） */
  showRegisterHint?: boolean;
  /**
   * 外部から企業データを渡したい場合に指定。
   * 指定された場合は内部のフェッチを行わず、このリストをそのまま表示する。
   */
  companies?: CompanyGridItem[];
  /** companies を指定した場合のローディング状態 */
  loading?: boolean;
  /** セクションヘッダーを非表示にしたい場合 */
  hideHeader?: boolean;
  /** カードが0件のとき表示するノードを差し替えたい場合 */
  emptyState?: React.ReactNode;
}

export const PublicCompanyGrid: React.FC<PublicCompanyGridProps> = ({
  onDetailsClick,
  mode = 'public',
  title,
  subtitle,
  limit,
  showRegisterHint = true,
  companies: externalCompanies,
  loading: externalLoading,
  hideHeader = false,
  emptyState = null,
}) => {
  const useExternal = externalCompanies !== undefined;
  const internal = usePublicCompanies(useExternal ? undefined : limit);

  const companies = useExternal ? externalCompanies! : internal.companies;
  const loading = useExternal ? !!externalLoading : internal.loading;
  const error = useExternal ? null : internal.error;

  if (error) return null;
  if (!loading && companies.length === 0) {
    return <>{emptyState}</>;
  }

  const resolvedTitle = title ?? (mode === 'public' ? '参加企業を見る' : '企業一覧');
  const resolvedSubtitle =
    subtitle ??
    (mode === 'public'
      ? '新規登録すると企業プロフィールの詳細を確認できます'
      : '気になる企業のプロフィールを確認できます');

  return (
    <Box sx={{ mt: hideHeader ? 0 : 5, mb: 2 }}>
      {/* セクションヘッダー */}
      {!hideHeader && (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            component="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.15rem', sm: '1.35rem' },
              color: '#2d2d2d',
              letterSpacing: '-0.01em',
            }}
          >
            {resolvedTitle}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.82rem', sm: '0.9rem' },
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {resolvedSubtitle}
          </Typography>
        </Box>
      )}

      {/* グリッド: public=Pinterest風(CSS columns) / authenticated=固定カード(CSS Grid) */}
      <Box
        sx={
          mode === 'public'
            ? {
                columns: { xs: 2, sm: 2, md: 3, lg: 4 },
                columnGap: { xs: 1.5, sm: 2 },
              }
            : {
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
                gap: { xs: 1.5, sm: 2 },
                alignItems: 'stretch',
              }
        }
      >
        {loading
          ? Array.from({ length: 8 }).map((_, i) => (
              <CardSkeleton key={i} mode={mode} />
            ))
          : companies.map((company, index) => (
              <CompanyCard
                key={company.id}
                company={company}
                onDetailsClick={onDetailsClick}
                gradientIndex={index}
                mode={mode}
              />
            ))}
      </Box>

      {/* ─── 新規登録誘導のサブテキスト（public モードのみ） ─── */}
      {mode === 'public' && showRegisterHint && !loading && companies.length > 0 && (
        <Box
          component="button"
          type="button"
          onClick={() => onDetailsClick(0)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            mt: 3,
            mx: 'auto',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            p: 0,
            '&:hover .register-hint': { color: '#667eea', opacity: 1 },
          }}
        >
          <Typography
            className="register-hint"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              color: 'text.disabled',
              transition: 'color 0.2s, opacity 0.2s',
            }}
          >
            もっと見るなら → 新規登録はこちら
          </Typography>
        </Box>
      )}
    </Box>
  );
};
